import { useState } from "react";
import Icon1 from "@assets/icons/Icon";
import type { WidgetConfig, WidgetId, RolePreset } from "types/dashboard";
import { WIDGET_CATALOG, getPresetLayout, getWidgetMeta } from "./widgetRegistry";

const PRESET_OPTIONS: { id: RolePreset; label: string; icon: string }[] = [
  { id: "receptionniste", label: "Réceptionnaire", icon: "side-reception" },
  { id: "atelier", label: "Atelier", icon: "side-atelier" },
  { id: "admin", label: "Administration", icon: "dashboard" },
  { id: "comptabilite", label: "Comptabilité", icon: "payments" },
];

const CATEGORY_LABELS: Record<string, string> = {
  stats: "Statistiques",
  ordres: "Ordres",
  finances: "Finances",
  clients: "Clients",
  equipe: "Équipe",
  planning: "Planning",
};

interface Props {
  widgets: WidgetConfig[];
  onSave: (widgets: WidgetConfig[]) => void;
  onClose: () => void;
}

export default function DashboardConfigModal({ widgets, onSave, onClose }: Props) {
  const [draft, setDraft] = useState<WidgetConfig[]>([...widgets]);
  const [dragIndex, setDragIndex] = useState<number | null>(null);

  const activeIds = new Set(draft.map((w) => w.id));

  function addWidget(id: WidgetId) {
    const meta = getWidgetMeta(id);
    if (!meta) return;
    setDraft((prev) => [...prev, { id, position: prev.length, size: meta.defaultSize }]);
  }

  function removeWidget(id: WidgetId) {
    setDraft((prev) => prev.filter((w) => w.id !== id).map((w, i) => ({ ...w, position: i })));
  }

  function moveWidget(fromIndex: number, toIndex: number) {
    setDraft((prev) => {
      const next = [...prev];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return next.map((w, i) => ({ ...w, position: i }));
    });
  }

  function applyPreset(preset: RolePreset) {
    setDraft(getPresetLayout(preset));
  }

  // Group available (inactive) widgets by category
  const availableByCategory: Record<string, typeof WIDGET_CATALOG> = {};
  for (const w of WIDGET_CATALOG) {
    if (!activeIds.has(w.id)) {
      if (!availableByCategory[w.category]) availableByCategory[w.category] = [];
      availableByCategory[w.category].push(w);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-700 max-h-[85vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex flex-row items-center justify-between px-6 py-4 border-b">
          <div className="flex flex-col">
            <div className="text-lg font-semibold text-black">Personnaliser le tableau de bord</div>
            <div className="text-h3 text-neutral-400">Choisissez et ordonnez vos widgets</div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-neutral-100">
            <Icon1 name="close" size={18} color="#999" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4 flex flex-col gap-6">
          {/* Presets */}
          <div className="flex flex-col gap-2">
            <div className="text-h2 font-medium text-neutral-500">Modèles prédéfinis</div>
            <div className="flex flex-row gap-2 flex-wrap">
              {PRESET_OPTIONS.map((p) => (
                <button
                  key={p.id}
                  onClick={() => applyPreset(p.id)}
                  className="flex flex-row items-center gap-2 px-4 py-2 rounded-lg border hover:border-neutral-400 hover:bg-neutral-50 transition-colors"
                >
                  <Icon1 name={p.icon} size={16} color="#6366F1" />
                  <span className="text-h2 font-medium text-black">{p.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Active widgets (reorderable) */}
          <div className="flex flex-col gap-2">
            <div className="text-h2 font-medium text-neutral-500">Widgets actifs</div>
            {draft.length === 0 && (
              <div className="text-h3 text-neutral-400 py-3">Aucun widget. Ajoutez-en depuis la liste ci-dessous.</div>
            )}
            <div className="flex flex-col gap-1">
              {draft.map((w, i) => {
                const meta = getWidgetMeta(w.id);
                if (!meta) return null;
                return (
                  <div
                    key={w.id}
                    draggable
                    onDragStart={() => setDragIndex(i)}
                    onDragOver={(e) => { e.preventDefault(); }}
                    onDrop={() => { if (dragIndex !== null && dragIndex !== i) moveWidget(dragIndex, i); setDragIndex(null); }}
                    onDragEnd={() => setDragIndex(null)}
                    className={`flex flex-row items-center gap-3 px-3 py-2.5 rounded-lg border transition-colors cursor-grab active:cursor-grabbing ${
                      dragIndex === i ? "border-indigo-300 bg-indigo-50" : "border-neutral-200 hover:border-neutral-300 bg-white"
                    }`}
                  >
                    <Icon1 name="drag" size={16} color="#CCC" />
                    <div className="flex flex-col flex-1 min-w-0">
                      <div className="text-h2 font-medium text-black">{meta.label}</div>
                      <div className="text-h4 text-neutral-400">{meta.description}</div>
                    </div>
                    <button
                      onClick={() => removeWidget(w.id)}
                      className="w-7 h-7 rounded-lg flex items-center justify-center hover:bg-red-50 transition-colors flex-shrink-0"
                    >
                      <Icon1 name="close" size={14} color="#EF4444" />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Available widgets */}
          {Object.keys(availableByCategory).length > 0 && (
            <div className="flex flex-col gap-2">
              <div className="text-h2 font-medium text-neutral-500">Widgets disponibles</div>
              {Object.entries(availableByCategory).map(([cat, widgets]) => (
                <div key={cat} className="flex flex-col gap-1">
                  <div className="text-h4 text-neutral-400 uppercase tracking-wide">{CATEGORY_LABELS[cat] || cat}</div>
                  {widgets.map((meta) => (
                    <div
                      key={meta.id}
                      className="flex flex-row items-center gap-3 px-3 py-2.5 rounded-lg border border-dashed border-neutral-200 hover:border-indigo-300 hover:bg-indigo-50/30 transition-colors cursor-pointer"
                      onClick={() => addWidget(meta.id)}
                    >
                      <div className="w-7 h-7 rounded-lg flex items-center justify-center bg-neutral-100 flex-shrink-0">
                        <Icon1 name={meta.icon} size={14} color="#6366F1" />
                      </div>
                      <div className="flex flex-col flex-1 min-w-0">
                        <div className="text-h2 font-medium text-black">{meta.label}</div>
                        <div className="text-h4 text-neutral-400">{meta.description}</div>
                      </div>
                      <div className="text-h3 text-indigo-500 font-medium flex-shrink-0">+ Ajouter</div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex flex-row items-center justify-end gap-3 px-6 py-4 border-t">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg border hover:bg-neutral-50 text-h2 font-medium text-neutral-600 transition-colors"
          >
            Annuler
          </button>
          <button
            onClick={() => onSave(draft)}
            className="px-4 py-2 rounded-lg bg-black text-white text-h2 font-medium hover:bg-neutral-800 transition-colors"
          >
            Enregistrer
          </button>
        </div>
      </div>
    </div>
  );
}
